/**
 * How a stored category is NAMED, anywhere it is shown (RESP2d).
 *
 * Label, icon and colour all come from the catalog, so renaming an area
 * in the settings page changes every task and every report that carries
 * it — without touching a single stored row. Before this slice they came
 * from a static frontend table of 13, which is why a rename was
 * invisible and an organisation's own area had no name at all.
 *
 * Three states, and each says something different:
 *
 * * **known** — label, icon, colour.
 * * **retired** — the same, plus the word "Inaktiv". Not a warning: the
 *   row is fine, the area simply is not offered for new work any more.
 * * **unresolvable** — a neutral phrase, never the raw key. This should
 *   be unreachable (the composite foreign key guarantees the area
 *   exists), so it means the catalog did not load — worth showing
 *   honestly rather than papering over with a slug.
 */
import React from "react";
import { Tag } from "lucide-react";

import { AREA_ICONS } from "../responsibilities/areaPresentation";
import useActivityCategoryCatalog, {
  UNASSIGNED_CATEGORY_LABEL,
  UNKNOWN_CATEGORY_LABEL,
} from "./useActivityCategoryCatalog";

const NEUTRAL_TOKEN =
  "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300";

/** Resolve a stored icon key. Never throws, never returns null. */
export function categoryIcon(iconKey) {
  return AREA_ICONS[iconKey] || Tag;
}

/**
 * The three display states, as data.
 *
 * Exported so a surface that needs its own layout — a table cell, a
 * detail card — can share the RESOLUTION without inheriting this chip's
 * markup.
 */
export function useCategoryDisplay(key) {
  const { getCategoryMeta, loading } = useActivityCategoryCatalog();
  if (!key) {
    return {
      state: "unassigned",
      label: UNASSIGNED_CATEGORY_LABEL,
      description: null,
      icon: Tag,
      colorToken: NEUTRAL_TOKEN,
      isInactive: false,
      loading,
    };
  }
  const meta = getCategoryMeta(key);
  if (!meta) {
    return {
      state: "unknown",
      label: UNKNOWN_CATEGORY_LABEL,
      description: null,
      icon: Tag,
      colorToken: NEUTRAL_TOKEN,
      isInactive: false,
      loading,
    };
  }
  return {
    state: "known",
    label: meta.label,
    description: meta.description || null,
    icon: categoryIcon(meta.icon),
    colorToken: meta.color_token || NEUTRAL_TOKEN,
    isInactive: meta.is_active === false,
    loading,
  };
}

export default function ActivityCategoryChip({ code, className = "" }) {
  const { label, icon: Icon, colorToken, isInactive } =
    useCategoryDisplay(code);

  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold " +
        colorToken +
        (className ? " " + className : "")
      }
    >
      <Icon aria-hidden="true" className="h-3 w-3" strokeWidth={2} />
      <span className="truncate">{label}</span>
      {isInactive ? (
        // A word, not a shade: an option list and a badge both have to
        // work for somebody who cannot see the colour.
        <span className="font-normal opacity-80">· Inaktiv</span>
      ) : null}
    </span>
  );
}
